import "reflect-metadata";

import { useEffect, useState } from "react";
import { API, Auth, I18n } from "aws-amplify";
import { CognitoHostedUIIdentityProvider } from "@aws-amplify/auth";
import { useRouter } from "next/router";
import { useDisclosure } from "@chakra-ui/react";

import { DIContainer } from "../../packages/idn-media-framework-tsyringe/DIContainer";
import { IEventBus } from "../../packages/idn-media-framework-abstraction/IEventBus";
import { IAuthenticationService } from "../../packages/idn-media-framework-abstraction/IAuthenticationService";
import { ILogger } from "../../packages/idn-media-framework-abstraction/ILogger";
import { CompositionRoot } from "../../src/CompositionRoot";
import { OAuthFlow } from "../../components/helpers/OAuthFlow";
import checkForceAuth from "../../components/helpers/checkForceAuth";

import LoadingPage from "@/components/LoadingPage";
import LandingPage from "@/components/Auth/LandingPage";
import { getIdentityType, userIdenityType } from "@/utility/identityType";
import { getOnboardingToggle } from "@/utility/onboarding";
import { CustomErrors } from "@/constants/customErrorStrings";

const handleIdPLogin = (identity_provider: any) => {
  const logger = DIContainer.Resolve<ILogger>("ILogger");
  let queryStringParams = new URLSearchParams(window.location.search);
  let redirect_uri = queryStringParams.get("redirect_uri");
  let authorization_code = queryStringParams.get("authorization_code");
  let clientState = queryStringParams.get("state");

  if (redirect_uri) {
    logger.debug("Storing redirect_uri to storage:", redirect_uri);
    localStorage.setItem(`client-redirect-uri`, redirect_uri);
  }
  if (authorization_code) {
    logger.debug("Storing authorization_code to storage:", authorization_code);
    localStorage.setItem(`authorization_code`, authorization_code);
  }
  if (clientState) {
    logger.debug("Storing clientState to storage:", clientState);
    localStorage.setItem(`client-state`, clientState);
  }

  localStorage.setItem(`social_identity_provider`, identity_provider);
  Auth.federatedSignIn({ provider: identity_provider });
};

let isInitialized = false;

const HomePage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [identity, setIdentity] = useState("");
  const [alertMsgSocmed, setAlertMsgSocmed] = useState("");
  const [user, setUser] = useState<null | any>(null);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [isNewPasswordRequiredChallange, setIsNewPasswordRequiredChallange] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(true);

  const router = useRouter();

  const handleIdpRelogin = async () => {
    setIsLoadingPage(true);
    
    const identity_provider = localStorage.getItem("social_identity_provider");
    const validProviders = [
      CognitoHostedUIIdentityProvider.Google,
      CognitoHostedUIIdentityProvider.Facebook,
      CognitoHostedUIIdentityProvider.Apple
    ];
    
    if (identity_provider && validProviders.includes(identity_provider as CognitoHostedUIIdentityProvider)) {
      await Auth.federatedSignIn({ provider: identity_provider as CognitoHostedUIIdentityProvider });
    } else {
      setIsLoadingPage(false);
    }
  };

  const init = async () => {
    if (isInitialized) {
      setIsLoadingPage(false);
    }

    await CompositionRoot.InitializeApplication();

    const eventBus = DIContainer.Resolve<IEventBus>("IEventBus");
    await eventBus.CreateTopic("LoginForm");

    await checkForceAuth();

    const loginFormSubscription = eventBus.Subscribe("LoginForm", async (e) => {
      if (e.eventName === "SocialLoginButtonClicked") {
        handleIdPLogin(e.data);
      }
    });

    const authenticationServiceSubscription = eventBus.Subscribe(
      "IAuthenticationService",
      async (e) => {
        if (e.eventName === "signIn") {
          setUser(e.data);

          try {
            setIsLoadingPage(true);
            const OAuthFlowSignIn = await new OAuthFlow().handleSignIn();

            const extraReq = {
              headers: {
                Authorization: `Bearer ${(await Auth.currentSession())
                  .getAccessToken()
                  .getJwtToken()}`,
              },
            };

            const response = await API.get("profile", "/detail", extraReq);

            const url = `/api/config/onboarding-toggle`;
            const config = await fetch(url)
              .then((response) => response.json())
              .catch((e) => console.log(e));

            const toggle = getOnboardingToggle(response.data, config);
            const needsOnboarding = response?.data?.is_onboarding === false && toggle === "on";
            
            if (OAuthFlowSignIn?.isOAuthFlow === false) {
              setIsLoadingPage(false);
              if (needsOnboarding && router.pathname !== "/onboarding") {
                router.push('/onboarding');
              } else {
                setIsLoggedIn(true);
              }
            } else if (OAuthFlowSignIn?.isOAuthFlow === true) {
              setIsLoadingPage(true);
              
              if (OAuthFlowSignIn.redirectUrl) {
                if (needsOnboarding) {
                  const encodedUrl = encodeURIComponent(OAuthFlowSignIn.redirectUrl);
                  window.location.replace(`/onboarding?redirect=${encodedUrl}`);
                } else {
                  window.location.replace(OAuthFlowSignIn.redirectUrl);
                }
              }
            }
          } catch (error: any) {
            if (error.response?.data == "Invalid authorization_code") {
              setIsLoggedIn(true);
              setIsLoadingPage(false);
            }
          }
        } else if (e.eventName === "signOut") {
          setIsLoggedIn(false);
          setUser(null);
          setIsNewPasswordRequiredChallange(false);
        } else {
          setIsLoadingPage(false);
        }
      }
    );

    const authenticationService = DIContainer.Resolve<IAuthenticationService>("IAuthenticationService");
    await authenticationService.CheckLoggedIn();

    if (
      router.query.error_description &&
      /already.found.an.entry.for.username/gi.test(router.query.error_description.toString())
    ) {
      handleIdpRelogin();
    }
  };

  const handleIdpSignIn = async (providerName: any) => {
    const logger = DIContainer.Resolve<ILogger>("ILogger");
    logger.debug("SocialLoginButton clickHandler called");

    const eventBus = DIContainer.Resolve<IEventBus>("IEventBus");
    eventBus.Dispatch("LoginForm", {
      eventName: "SocialLoginButtonClicked",
      data: providerName,
      message: "",
    });
  };

  useEffect(() => {
    if (router.query.error_description) {
      const errorDesc = router.query.error_description.toString().trim();

      const standardErrors = [
        CustomErrors.DISABLE_EMAIL_PERMISSION_FACEBOOK,
        CustomErrors.DISABLE_EMAIL_PERMISSION_FACEBOOK_PRESIGNUP_TRIGGER,
        CustomErrors.USER_ALREADY_DELETED,
        CustomErrors.USER_ALREADY_DELETED_REGISTER_IDNACCOUNT,
        CustomErrors.USER_ALREADY_DELETED_SIGNIN_IDNACCOUNT
      ];

      if (standardErrors.includes(errorDesc)) {
        setAlertMsgSocmed(I18n.get(errorDesc));
      } else if (errorDesc === CustomErrors.USER_ALREADY_EXIST_REGISTER_IDNACCOUNT) {
        const isPhoneNumber = getIdentityType(identity) === userIdenityType.PHONE_NUMBER;
        const errorKey = isPhoneNumber 
          ? CustomErrors.PHONE_NUMBER_ALREADY_EXIST_REGISTER
          : CustomErrors.EMAIL_ALREADY_EXIST_REGISTER;
        setAlertMsgSocmed(I18n.get(errorKey));
      } else if (/password.reset.required.for.the.user/gi.test(errorDesc)) {
        setAlertMsgSocmed(CustomErrors.SOCMED_SIGNIN_RESET_REQUIRED);
      } else if (
        errorDesc == CustomErrors.ACCESS_DENIED_FACEBOOK ||
        errorDesc == CustomErrors.CANCELLED_AUTHORIZE_APPLE ||
        /already.found.an.entry.for.username/gi.test(errorDesc)
      ) {
        return;
      } else if (errorDesc.includes("kamu melebihi batas device")) {
        setAlertMsgSocmed("Akun Anda telah mencapai batas maksimum login. Keluar dari perangkat lain untuk melanjutkan.");
      } else if (errorDesc.includes("user already banned")) {
        setIsLoadingPage(false);
        onOpen();
      } else {
        setAlertMsgSocmed(CustomErrors.DEFAULT_MSG);
      }
    }
  }, [router.isReady, router.query.error, router.query.error_description]);

  const handleLogout = async () => {
    const service = DIContainer.Resolve<IAuthenticationService>("IAuthenticationService");
    await service.Logout();

    await router.push("/signout");

    setIsLoggedIn(false);
    setUser(null);
    setIsNewPasswordRequiredChallange(false);
  };

  useEffect(() => {
    init();
    isInitialized = true;
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      router.push("/my-account");
    }
  }, [isLoggedIn, router.push, router.asPath]);

  return (
    <>
      {(isLoggedIn || isLoadingPage || isLoggedIn === undefined) && (
        <LoadingPage />
      )}
      {isLoggedIn === false && isLoadingPage === false && (
        <LandingPage
          isOpenModal={isOpen}
          onCloseModal={onClose}
          onOpenModal={onOpen}
          handleIdpSignIn={handleIdpSignIn}
          alertMessageSocmed={alertMsgSocmed}
        />
      )}
    </>
  );
};

export default HomePage;
